/**
 * Schedule scope modal: create new block from a grid cell, or edit catalog work scope (settings).
 * Create flow is a single form step; default crew is set from the timeline afterward.
 */
import { useEffect, useMemo, useState } from "react";
import { parsePositiveInt } from "../../../../utils/common/formatting/numbers";
import { ModalWrapper } from "../../../../components/ui";
import { Button } from "../../../../components/ui";
import CrewCard from "../../shared/CrewCard";
import { PLANNING_TOOLTIP_MESSAGES } from "../../../../config";
import { useScheduleContext } from "../../../../context/domain/planning/ScheduleContext";
import { sameScheduleBlockId } from "../model/scheduleIds";

const TITLE_ID = "schedule-scope-modal-title";

/**
 * Create a scope block from a grid cell or edit catalog work scope on an existing block.
 *
 * @param {Object} props
 * @param {boolean} props.open When true, the modal is visible.
 * @param {'create'|'settings'} [props.variant] `create` shows span controls; `settings` edits work scope.
 * @param {string} [props.title] Modal title override.
 * @param {boolean} [props.defaultWorkingDaysOnly] Default for the “working days only” checkbox (create).
 * @param {Array<Object>} props.workScopes Work-scope catalog options.
 * @param {string|number} [props.initialWorkScopeId] Pre-selected scope id (settings).
 * @param {string|number|null} [props.scheduleBlockId] Persisted block id for settings {@link CrewCard}.
 * @param {() => void} props.onClose Close without saving.
 * @param {(payload: { workScopeId: string, days: number, workingDaysOnly: boolean }) => void} [props.onCreate] Create submit (create variant).
 * @param {(workScopeId: string) => void|Promise<void>} [props.onConfirmSettings] Settings submit.
 * @returns {JSX.Element}
 */
export default function ScheduleScopeModal({
  open,
  variant = "create",
  title,
  defaultWorkingDaysOnly = true,
  workScopes,
  initialWorkScopeId,
  scheduleBlockId = null,
  onClose,
  onCreate,
  onConfirmSettings,
}) {
  const resolvedTitle =
    title ??
    (variant === "settings" ? "Scope Settings" : "Add scope");

  const {
    blocks,
    handleOpenScopeCrewModal,
    requestDeleteScopeBlock,
    deleteScopeBlockBusy,
    blockDraftWorkers,
  } = useScheduleContext();

  const firstId = workScopes[0]?.id ?? "";
  const [workScopeId, setWorkScopeId] = useState(firstId);
  const [days, setDays] = useState("1");
  const [workingDaysOnly, setWorkingDaysOnly] = useState(defaultWorkingDaysOnly);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }
    if (variant === "settings" && initialWorkScopeId != null) {
      setWorkScopeId(String(initialWorkScopeId));
    } else {
      setWorkScopeId(workScopes[0]?.id ?? "");
    }
    setDays("1");
    setWorkingDaysOnly(defaultWorkingDaysOnly);
    setError("");
  }, [open, workScopes, defaultWorkingDaysOnly, variant, initialWorkScopeId]);

  const canSubmit = useMemo(
    () => Boolean(workScopeId && workScopes.length),
    [workScopeId, workScopes.length]
  );

  const showSpanControls = variant === "create";

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    const d = parsePositiveInt(days, 0);
    if (d < 1) {
      setError("Days must be a positive whole number.");
      return;
    }
    if (!workScopeId) {
      setError("Select a work scope.");
      return;
    }
    onCreate?.({ workScopeId, days: d, workingDaysOnly });
  };

  const handleSettingsConfirm = () => {
    if (!workScopeId) {
      setError("Select a work scope.");
      return;
    }
    setError("");
    onConfirmSettings?.({ workScopeId });
  };

  const handleRequestDeleteScopeBlock = () => {
    if (deleteScopeBlockBusy || !scheduleBlockId) {
      return;
    }
    const block = blocks.find((b) =>
      sameScheduleBlockId(b.id, scheduleBlockId)
    );
    if (!block) {
      return;
    }
    requestDeleteScopeBlock(block);
    onClose?.();
  };

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      closeButton={false}
      maxWidth={400}
      overlayClassName="z-[10050]"
      contentProps={{ "data-schedule-scope-modal": "" }}
      outsideClickIgnoreSelector="[data-schedule-worker-select-modal]"
    >
      <div role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
        <div className="border-b border-gray-200 px-4 py-3">
          <h2
            id={TITLE_ID}
            className="text-base font-semibold text-gray-900"
          >
            {resolvedTitle}
          </h2>
        </div>

        <form onSubmit={variant === "settings" ? (e) => e.preventDefault() : handleCreateSubmit} className="space-y-4 px-4 py-4 pt-0">
          <div>
            <label
              htmlFor="schedule-work-scope"
              className="block text-sm font-medium text-gray-700"
            >
              Work scope
            </label>
            <select
              id="schedule-work-scope"
              className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
              value={workScopeId}
              onChange={(e) => setWorkScopeId(e.target.value)}
            >
              {workScopes.map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws.name ?? ws.title ?? ""}
                </option>
              ))}
            </select>
          </div>

          {showSpanControls ? (
            <>
              <div className="flex items-start gap-2">
                <input
                  id="schedule-working-days-only"
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300"
                  checked={workingDaysOnly}
                  onChange={(e) => setWorkingDaysOnly(e.target.checked)}
                />
                <label
                  htmlFor="schedule-working-days-only"
                  className="text-sm text-gray-700"
                >
                  <span className="font-medium">Working days only</span>
                  <span className="block text-xs font-normal text-gray-500">
                    Block will skip weekends.
                  </span>
                </label>
              </div>

              <div>
                <label
                  htmlFor="schedule-days"
                  className="block text-sm font-medium text-gray-700"
                >
                  {workingDaysOnly ? "Working Days" : "Calendar Days"}
                </label>
                <input
                  id="schedule-days"
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {workingDaysOnly
                    ? "Mon–Fri Only."
                    : "Includes weekends."}
                </p>
              </div>
            </>
          ) : null}
          <CrewCard
            scheduleBlockId={scheduleBlockId}
            workers={blockDraftWorkers.current ? blockDraftWorkers.current : []}
            title="Default Crew"
            variant="default"
            editTooltip={PLANNING_TOOLTIP_MESSAGES.editToAddCrew}
            onEditClick={(e) => {
              e.stopPropagation();
              const block = blocks.find((b) =>
                sameScheduleBlockId(b.id, scheduleBlockId)
              );
              if (block) {
                handleOpenScopeCrewModal(block, block.startDate, {
                  fromScopeSettings: true,
                });
              } else {
                handleOpenScopeCrewModal(null, null, {
                  fromScopeSettings: true,
                });
              }
            }}
          />
          {error ? (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          ) : null}

          <div
            className={`flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4 ${
              variant === "settings" && Boolean(scheduleBlockId)
                ? "justify-between"
                : "justify-end"
            }`}
          >
            {variant === "settings" && scheduleBlockId ? (
              <Button
                variant="custom"
                className="border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 shadow-sm hover:bg-red-50"
                disabled={deleteScopeBlockBusy}
                onClick={handleRequestDeleteScopeBlock}
                text="Delete scope…"
              />
            ) : null}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="custom"
                className="border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-100"
                onClick={onClose}
                text="Cancel"
              />
              {variant === "settings" ? (
                <Button
                  variant="custom"
                  className="bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800"
                  disabled={!canSubmit}
                  onClick={handleSettingsConfirm}
                  text="Confirm"
                />
              ) : (
                <Button
                  variant="custom"
                  className="bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800"
                  disabled={!canSubmit}
                  onClick={handleCreateSubmit}
                  text="Create"
                />
              )}
            </div>
          </div>
        </form>
      </div>
    </ModalWrapper>
  );
}
